import React, { useState, useEffect, useCallback } from 'react';
import styles from './ParticipationModule.module.css';
import { Project } from '../../types';
import { participationAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface ParticipationModuleProps {
  project: Project;
}

interface ParticipationItem {
  _id: string;
  title: string;
  description?: string;
  type: 'survey' | 'poll' | 'idea_collection' | 'voting' | 'open_consultation';
  status: 'draft' | 'active' | 'closed' | 'archived';
  participationLevel: string;
  questions: any[];
  responses: any[];
  analytics: {
    totalResponses: number;
    lastResponseAt?: string;
  };
  scheduling?: {
    startDate?: string;
    endDate?: string;
  };
  settings?: {
    allowAnonymous?: boolean;
    requireEmail?: boolean;
    showResults?: boolean;
    maxResponses?: number;
  };
  createdAt: string;
}

interface ResultsData {
  totalResponses: number;
  questions: any[];
  timeline: { date: string; count: number }[];
  sources: { source: string; count: number }[];
}

const TYPE_LABELS: Record<string, string> = {
  survey: 'Enquete',
  poll: 'Peiling',
  idea_collection: 'Ideeen',
  voting: 'Stemming',
  open_consultation: 'Open Consultatie'
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Concept',
  active: 'Actief',
  closed: 'Gesloten',
  archived: 'Gearchiveerd'
};

const LEVEL_LABELS: Record<string, string> = {
  informeren: 'Informeren',
  consulteren: 'Consulteren',
  adviseren: 'Adviseren',
  coproduceren: 'Coproduceren',
  meebeslissen: 'Meebeslissen'
};

export const ParticipationModule: React.FC<ParticipationModuleProps> = ({ project }) => {
  const toast = useToast();
  const [items, setItems] = useState<ParticipationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'create' | 'detail' | 'results'>('list');
  const [selectedItem, setSelectedItem] = useState<ParticipationItem | null>(null);
  const [results, setResults] = useState<ResultsData | null>(null);
  const [filter, setFilter] = useState<string>('all');

  // Create form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'survey' as string,
    participationLevel: 'consulteren',
    questions: [] as any[],
    settings: {
      allowAnonymous: true,
      requireEmail: false,
      showResults: false,
      maxResponses: undefined as number | undefined
    }
  });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await participationAPI.getByProject(project.id);
      setItems(data.participations || []);
    } catch (error) {
      console.error('Failed to fetch participations:', error);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCreate = async () => {
    try {
      await participationAPI.create({ ...formData, project: project.id });
      toast.success('Participatie aangemaakt');
      setView('list');
      setFormData({ title: '', description: '', type: 'survey', participationLevel: 'consulteren', questions: [], settings: { allowAnonymous: true, requireEmail: false, showResults: false, maxResponses: undefined } });
      fetchItems();
    } catch (error) {
      toast.error('Fout bij aanmaken');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await participationAPI.activate(id);
      toast.success('Participatie geactiveerd');
      fetchItems();
    } catch (error: any) {
      toast.error('Fout', error.response?.data?.error || 'Kan niet activeren');
    }
  };

  const handleClose = async (id: string) => {
    try {
      await participationAPI.close(id);
      toast.success('Participatie gesloten');
      fetchItems();
    } catch (error) {
      toast.error('Fout bij sluiten');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await participationAPI.delete(id);
      toast.success('Participatie verwijderd');
      fetchItems();
      if (selectedItem?._id === id) setView('list');
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleViewResults = async (item: ParticipationItem) => {
    try {
      const data = await participationAPI.getResults(item._id);
      setResults(data);
      setSelectedItem(item);
      setView('results');
    } catch (error) {
      toast.error('Fout bij laden resultaten');
    }
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, {
        text: '',
        type: 'text',
        required: false,
        options: [],
        order: prev.questions.length
      }]
    }));
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const removeQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const filteredItems = filter === 'all' ? items : items.filter(i => i.status === filter);

  if (loading) {
    return <div className={styles.container}><div className={styles.loading}>Laden...</div></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Participatie</h1>
          <p className={styles.subtitle}>Enquetes, peilingen en ideeen-inzameling</p>
        </div>
        {view === 'list' && (
          <button className={styles.primaryButton} onClick={() => setView('create')}>
            + Nieuwe Participatie
          </button>
        )}
        {view !== 'list' && (
          <button className={styles.secondaryButton} onClick={() => { setView('list'); setSelectedItem(null); }}>
            Terug naar overzicht
          </button>
        )}
      </div>

      {view === 'list' && (
        <>
          {/* Stats */}
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{items.length}</span>
              <span className={styles.statLabel}>Totaal</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{items.filter(i => i.status === 'active').length}</span>
              <span className={styles.statLabel}>Actief</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{items.reduce((sum, i) => sum + (i.analytics?.totalResponses || 0), 0)}</span>
              <span className={styles.statLabel}>Reacties</span>
            </div>
          </div>

          {/* Filters */}
          <div className={styles.filters}>
            {['all', 'draft', 'active', 'closed'].map(f => (
              <button key={f} className={`${styles.filterButton} ${filter === f ? styles.filterActive : ''}`} onClick={() => setFilter(f)}>
                {f === 'all' ? 'Alles' : STATUS_LABELS[f]}
              </button>
            ))}
          </div>

          {/* List */}
          {filteredItems.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>
              <h3>Geen participaties gevonden</h3>
              <p>Maak een nieuwe enquete, peiling of consultatie aan.</p>
            </div>
          ) : (
            <div className={styles.list}>
              {filteredItems.map(item => (
                <div key={item._id} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div>
                      <h3 className={styles.cardTitle}>{item.title}</h3>
                      <div className={styles.cardMeta}>
                        <span className={`${styles.badge} ${styles[`badge_${item.status}`]}`}>{STATUS_LABELS[item.status]}</span>
                        <span className={styles.typeBadge}>{TYPE_LABELS[item.type]}</span>
                        <span className={styles.levelBadge}>{LEVEL_LABELS[item.participationLevel]}</span>
                      </div>
                    </div>
                    <div className={styles.cardStats}>
                      <span className={styles.responseCount}>{item.analytics?.totalResponses || 0}</span>
                      <span className={styles.responseLabel}>reacties</span>
                    </div>
                  </div>
                  {item.description && <p className={styles.cardDescription}>{item.description}</p>}
                  <div className={styles.cardFooter}>
                    <span className={styles.cardDate}>
                      {item.questions?.length || 0} vragen
                      {item.scheduling?.endDate && ` | Sluit ${new Date(item.scheduling.endDate).toLocaleDateString('nl-NL')}`}
                    </span>
                    <div className={styles.cardActions}>
                      {item.status === 'draft' && (
                        <button className={styles.actionButton} onClick={() => handleActivate(item._id)}>Activeren</button>
                      )}
                      {item.status === 'active' && (
                        <button className={styles.actionButton} onClick={() => handleClose(item._id)}>Sluiten</button>
                      )}
                      <button className={styles.actionButton} onClick={() => handleViewResults(item)}>Resultaten</button>
                      <button className={styles.actionButtonDanger} onClick={() => handleDelete(item._id)}>Verwijderen</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'create' && (
        <div className={styles.createForm}>
          <h2>Nieuwe Participatie</h2>

          <div className={styles.formGroup}>
            <label>Titel *</label>
            <input type="text" value={formData.title} onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Bijv. Enquete bewonerstevredenheid" />
          </div>

          <div className={styles.formGroup}>
            <label>Beschrijving</label>
            <textarea value={formData.description} onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Wat is het doel van deze participatie?" rows={3} />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Type</label>
              <select value={formData.type} onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Participatieniveau</label>
              <select value={formData.participationLevel} onChange={e => setFormData(prev => ({ ...prev, participationLevel: e.target.value }))}>
                {Object.entries(LEVEL_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Questions builder */}
          <div className={styles.questionsSection}>
            <div className={styles.questionsSectionHeader}>
              <h3>Vragen ({formData.questions.length})</h3>
              <button className={styles.addButton} onClick={addQuestion}>+ Vraag toevoegen</button>
            </div>

            {formData.questions.map((q, idx) => (
              <div key={idx} className={styles.questionCard}>
                <div className={styles.questionHeader}>
                  <span className={styles.questionNumber}>Vraag {idx + 1}</span>
                  <button className={styles.removeButton} onClick={() => removeQuestion(idx)}>Verwijderen</button>
                </div>
                <input type="text" value={q.text} onChange={e => updateQuestion(idx, 'text', e.target.value)} placeholder="Vraagtekst" className={styles.questionInput} />
                <div className={styles.questionOptions}>
                  <select value={q.type} onChange={e => updateQuestion(idx, 'type', e.target.value)}>
                    <option value="text">Open tekst</option>
                    <option value="rating">Waardering (1-5)</option>
                    <option value="single_choice">Enkele keuze</option>
                    <option value="multiple_choice">Meerkeuze</option>
                  </select>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" checked={q.required} onChange={e => updateQuestion(idx, 'required', e.target.checked)} />
                    Verplicht
                  </label>
                </div>
                {['single_choice', 'multiple_choice'].includes(q.type) && (
                  <div className={styles.optionsBuilder}>
                    <label>Opties (1 per regel)</label>
                    <textarea value={(q.options || []).join('\n')} onChange={e => updateQuestion(idx, 'options', e.target.value.split('\n').filter(Boolean))} placeholder="Optie 1&#10;Optie 2&#10;Optie 3" rows={4} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Settings */}
          <div className={styles.settingsSection}>
            <h3>Instellingen</h3>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={formData.settings.allowAnonymous} onChange={e => setFormData(prev => ({ ...prev, settings: { ...prev.settings, allowAnonymous: e.target.checked } }))} />
              Anonieme reacties toestaan
            </label>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={formData.settings.showResults} onChange={e => setFormData(prev => ({ ...prev, settings: { ...prev.settings, showResults: e.target.checked } }))} />
              Resultaten tonen aan deelnemers
            </label>
          </div>

          <div className={styles.formActions}>
            <button className={styles.secondaryButton} onClick={() => setView('list')}>Annuleren</button>
            <button className={styles.primaryButton} onClick={handleCreate} disabled={!formData.title}>Aanmaken</button>
          </div>
        </div>
      )}

      {view === 'results' && selectedItem && results && (
        <div className={styles.resultsView}>
          <h2>{selectedItem.title} - Resultaten</h2>
          <div className={styles.resultsStats}>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{results.totalResponses}</span>
              <span className={styles.statLabel}>Totaal reacties</span>
            </div>
          </div>

          {results.questions.map((q: any, idx: number) => (
            <div key={idx} className={styles.resultQuestion}>
              <h4>{q.questionText}</h4>
              <p className={styles.resultMeta}>{q.totalAnswers} antwoorden | Type: {q.type}</p>

              {q.type === 'rating' && q.data && (
                <div className={styles.ratingResult}>
                  <div className={styles.ratingAverage}>Gemiddelde: <strong>{Number(q.data.average).toFixed(1)}</strong></div>
                  <div className={styles.ratingBars}>
                    {q.data.distribution?.map((d: any) => (
                      <div key={d.value} className={styles.ratingBar}>
                        <span>{d.value}</span>
                        <div className={styles.barTrack}>
                          <div className={styles.barFill} style={{ width: `${q.totalAnswers > 0 ? (d.count / q.totalAnswers * 100) : 0}%` }} />
                        </div>
                        <span>{d.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {['single_choice', 'multiple_choice'].includes(q.type) && q.data?.options && (
                <div className={styles.choiceResult}>
                  {q.data.options.map((opt: any, optIdx: number) => (
                    <div key={optIdx} className={styles.choiceOption}>
                      <div className={styles.choiceHeader}>
                        <span>{opt.option}</span>
                        <span>{opt.count} ({opt.percentage}%)</span>
                      </div>
                      <div className={styles.barTrack}>
                        <div className={styles.barFill} style={{ width: `${opt.percentage}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {q.type === 'text' && q.data?.responses && (
                <div className={styles.textResponses}>
                  {q.data.responses.slice(0, 10).map((resp: string, rIdx: number) => (
                    <div key={rIdx} className={styles.textResponse}>{resp}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
